// TUT-450 — configurações do escritório: envios agendados, configurações e a IA.
import { demoApi } from "../demo-data";
import type {
  AiDraft,
  AiStatus,
  ConfiguracaoDeIa,
  ConsumoDeIaDoMes,
  EstadoDaExtracao,
  ResultadoDaExtracao,
  ResultadoDoTesteDeIa,
  ResumoDoProcessoResposta,
  SalvarConfiguracaoDeIaInput,
  SaveScheduledJobInput,
  ScheduledJob,
  TenantSetting,
  ScheduleTimeZone,
} from "../types";
import { request } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

export const apiDasConfiguracoes = {
  // Agendamentos do escritório (TUT-136): o que está ligado, quando roda e
  // como terminou da última vez.
  scheduledJobs(): Promise<{
    items: ScheduledJob[];
    timeZones: ScheduleTimeZone[];
  }> {
    if (useDemoApi) return demoApi.scheduledJobs();
    return request<{ items: ScheduledJob[]; timeZones: ScheduleTimeZone[] }>(
      "/scheduled-jobs",
    );
  },
  saveScheduledJob(
    jobKey: string,
    input: SaveScheduledJobInput,
  ): Promise<ScheduledJob> {
    if (useDemoApi) return demoApi.saveScheduledJob(jobKey, input);
    return request<ScheduledJob>(
      `/scheduled-jobs/${encodeURIComponent(jobKey)}`,
      { method: "PUT", body: JSON.stringify(input) },
    );
  },
  // Configurações do escritório (TUT-137 fase 2). A lista já vem filtrada pela
  // permissão de cada chave — o servidor decide o que esta pessoa pode ver, e a
  // tela não tem de adivinhar.
  tenantSettings(): Promise<{ items: TenantSetting[] }> {
    if (useDemoApi) return demoApi.tenantSettings();
    return request<{ items: TenantSetting[] }>("/tenant-settings");
  },
  saveTenantSetting(key: string, value: unknown): Promise<TenantSetting> {
    if (useDemoApi) return demoApi.saveTenantSetting(key, value);
    return request<TenantSetting>(
      `/tenant-settings/${encodeURIComponent(key)}`,
      { method: "PUT", body: JSON.stringify({ value }) },
    );
  },
  // TUT-514 — Configurações › Inteligência artificial (só `ia.configurar`). A
  // chave vai no PUT e nunca volta: a leitura traz só os 4 últimos caracteres.
  configuracaoDeIa(): Promise<ConfiguracaoDeIa> {
    if (useDemoApi) return demoApi.configuracaoDeIa();
    return request<ConfiguracaoDeIa>("/ia/configuracao");
  },
  salvarConfiguracaoDeIa(input: SalvarConfiguracaoDeIaInput): Promise<ConfiguracaoDeIa> {
    if (useDemoApi) return demoApi.salvarConfiguracaoDeIa(input);
    return request<ConfiguracaoDeIa>("/ia/configuracao", { method: "PUT", body: JSON.stringify(input) });
  },
  removerConfiguracaoDeIa(): Promise<ConfiguracaoDeIa> {
    if (useDemoApi) return demoApi.removerConfiguracaoDeIa();
    return request<ConfiguracaoDeIa>("/ia/configuracao", { method: "DELETE" });
  },
  testarConfiguracaoDeIa(): Promise<ResultadoDoTesteDeIa> {
    if (useDemoApi) return demoApi.testarConfiguracaoDeIa();
    return request<ResultadoDoTesteDeIa>("/ia/configuracao/testar", { method: "POST" });
  },
  consumoDaIa(mes?: string): Promise<ConsumoDeIaDoMes> {
    if (useDemoApi) return demoApi.consumoDaIa(mes);
    return request<ConsumoDeIaDoMes>(`/ia/configuracao/consumo${mes ? `?mes=${encodeURIComponent(mes)}` : ""}`);
  },
  // TUT-500 — o resumo do processo por IA, na ficha. Ler é de quem vê o
  // processo; gerar (`ia.resumir_processo`) é sob pedido e substitui o último.
  resumoDoProcesso(processId: string): Promise<ResumoDoProcessoResposta> {
    if (useDemoApi) return demoApi.resumoDoProcesso(processId);
    return request<ResumoDoProcessoResposta>(`/processes/${encodeURIComponent(processId)}/resumo-ia`);
  },
  gerarResumoDoProcesso(processId: string): Promise<ResumoDoProcessoResposta> {
    if (useDemoApi) return demoApi.gerarResumoDoProcesso(processId);
    return request<ResumoDoProcessoResposta>(`/processes/${encodeURIComponent(processId)}/resumo-ia`, { method: "POST" });
  },
  // TUT-132 fase 3 — a extração da petição para o roteiro: sugestões, nunca gravadas aqui.
  estadoDaExtracaoDaPeticao(processId: string): Promise<EstadoDaExtracao> {
    if (useDemoApi) return demoApi.estadoDaExtracaoDaPeticao(processId);
    return request<EstadoDaExtracao>(`/processes/${encodeURIComponent(processId)}/extracao-ia`);
  },
  extrairDaPeticao(processId: string, texto: string): Promise<ResultadoDaExtracao> {
    if (useDemoApi) return demoApi.extrairDaPeticao(processId, texto);
    return request<ResultadoDaExtracao>(`/processes/${encodeURIComponent(processId)}/extracao-ia`, {
      method: "POST",
      body: JSON.stringify({ texto }),
    });
  },
  // Minuta de laudo por IA (TUT-132 fase 1). O status existe porque desligado
  // é desligado VISÍVEL: sem a IA configurada no escritório (TUT-514), a tela
  // explica em vez de oferecer um botão que falha.
  aiStatus(): Promise<AiStatus> {
    if (useDemoApi) return demoApi.aiStatus();
    return request<AiStatus>("/ai/status");
  },
  aiDrafts(
    expertiseId: string,
    reportId: string,
  ): Promise<{ items: AiDraft[] }> {
    if (useDemoApi) return demoApi.aiDrafts(reportId);
    return request<{ items: AiDraft[] }>(
      `/expertises/${encodeURIComponent(expertiseId)}/reports/${encodeURIComponent(reportId)}/ai-drafts`,
    );
  },
  generateAiDraft(
    expertiseId: string,
    reportId: string,
    input: { processId: string; templateId?: string },
  ): Promise<AiDraft> {
    if (useDemoApi) return demoApi.generateAiDraft(reportId, input);
    return request<AiDraft>(
      `/expertises/${encodeURIComponent(expertiseId)}/reports/${encodeURIComponent(reportId)}/ai-drafts`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
};
